import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';

import { apiService } from '../../services/apiService';
import { getCachedData, setCachedData } from '../../utils/pageCache';
import { parseCalendarDate } from '../../utils/calendarDate';
import type {
  PurchaseOrderReceipt,
  PurchaseOrderReceiptLine,
} from '../../services/types';

interface PurchaseOrderReceiptsTableProps {
  poNum: number;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const date = parseCalendarDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-CA');
}

function formatQty(value: number): string {
  return Number(value.toFixed(2)).toString();
}

const RECEIVED_TO_KEYS: Record<
  PurchaseOrderReceiptLine['receivedTo'],
  [string, string]
> = {
  stock: ['purchaseOrder.receivedToStock', 'Stock'],
  inspection: ['purchaseOrder.receivedToInspection', 'Inspection'],
  subcontract: ['purchaseOrder.receivedToSubcontract', 'Subcontract'],
  other: ['purchaseOrder.receivedToOther', 'Other'],
};

const receiptKey = (receipt: PurchaseOrderReceipt) =>
  `${receipt.packSlip}|${receipt.receiptDate}`;

/**
 * The receipts of a purchase order, loaded on their own so a failure here doesn't hide the PO
 */
export const PurchaseOrderReceiptsTable: React.FC<
  PurchaseOrderReceiptsTableProps
> = ({ poNum }) => {
  const { t } = useTranslation();
  const [receipts, setReceipts] = useState<PurchaseOrderReceipt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `purchaseOrderReceipts_${poNum}`;
    const cached = getCachedData<PurchaseOrderReceipt[]>(cacheKey);
    setExpanded(new Set());
    if (cached) {
      setReceipts(cached);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    apiService
      .getPurchaseOrderReceipts(poNum)
      .then((data) => {
        if (cancelled) return;
        setReceipts(data);
        setCachedData(cacheKey, data);
      })
      .catch((err) => {
        if (cancelled) return;
        setReceipts([]);
        setError(
          err instanceof Error ? err.message : 'Failed to fetch receipts'
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [poNum]);

  const copyPackSlip = (key: string, packSlip: string) => {
    navigator.clipboard.writeText(packSlip);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const toggleReceipt = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const headerCellClass =
    'px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight';
  const detailHeaderCellClass =
    'px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight';

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm">
      <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
        <h2 className="text-lg font-bold text-blue-900">
          {t('purchaseOrder.receipts', 'Receipts')}
          {!isLoading && !error && ` (${receipts.length})`}
        </h2>
      </div>

      {isLoading && (
        <div className="p-8 text-center text-gray-500">
          {t('purchaseOrder.loadingReceipts', 'Loading receipts...')}
        </div>
      )}

      {error && !isLoading && (
        <div className="p-4 text-sm text-red-700">
          {t('purchaseOrder.receiptsError', 'Failed to load receipts')}: {error}
        </div>
      )}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="w-10 px-2 py-3" />
                <th className={headerCellClass}>
                  {t('purchaseOrder.packSlip', 'Packing Slip')}
                </th>
                <th className={headerCellClass}>
                  {t('purchaseOrder.receiptDate', 'Receipt Date')}
                </th>
                <th className={headerCellClass}>
                  {t('purchaseOrder.receivedBy', 'Received By')}
                </th>
                <th className={headerCellClass}>
                  {t('purchaseOrder.shipVia', 'Ship Via')}
                </th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((receipt) => {
                const key = receiptKey(receipt);
                const isExpanded = expanded.has(key);
                const packSlip = receipt.packSlip.toUpperCase();

                return (
                  <React.Fragment key={key}>
                    <tr
                      onClick={() => toggleReceipt(key)}
                      className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                        isExpanded ? 'bg-blue-50/40' : ''
                      }`}
                    >
                      <td className="px-2 py-3 text-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceipt(key);
                          }}
                          aria-expanded={isExpanded}
                          aria-label={t(
                            'common.expandCollapse',
                            'Expand/Collapse'
                          )}
                          className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown size={16} className="text-gray-600" />
                          ) : (
                            <ChevronRight size={16} className="text-gray-600" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        {packSlip ? (
                          <span className="inline-flex items-center gap-1.5">
                            {packSlip}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyPackSlip(key, packSlip);
                              }}
                              title={
                                copiedKey === key
                                  ? t('purchaseOrder.copied', 'Copied!')
                                  : t(
                                      'purchaseOrder.copyPackSlip',
                                      'Copy packing slip'
                                    )
                              }
                              aria-label={t(
                                'purchaseOrder.copyPackSlip',
                                'Copy packing slip'
                              )}
                              className="p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                            >
                              {copiedKey === key ? (
                                <Check size={14} className="text-green-600" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {formatDate(receipt.receiptDate)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {receipt.receivedBy || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {receipt.shipVia || '—'}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-gray-200">
                        <td colSpan={5} className="bg-gray-50 px-4 py-3">
                          <div className="ml-6 overflow-x-auto rounded border border-gray-200 bg-white">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-100 border-b border-gray-200">
                                  <th className={detailHeaderCellClass}>
                                    {t('purchaseOrder.line', 'Line')}
                                  </th>
                                  <th className={detailHeaderCellClass}>
                                    {t('purchaseOrder.partNum', 'Part #')}
                                  </th>
                                  <th className={detailHeaderCellClass}>
                                    {t(
                                      'purchaseOrder.description',
                                      'Description'
                                    )}
                                  </th>
                                  <th
                                    className={`${detailHeaderCellClass} text-right`}
                                  >
                                    {t('purchaseOrder.qty', 'Qty')}
                                  </th>
                                  <th className={detailHeaderCellClass}>
                                    {t(
                                      'purchaseOrder.receivedTo',
                                      'Received To'
                                    )}
                                  </th>
                                  <th className={detailHeaderCellClass}>
                                    {t('purchaseOrder.binOrJob', 'Bin / Job')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {receipt.lines.map((line) => (
                                  <tr
                                    key={line.packLine}
                                    className="border-b border-gray-100 last:border-b-0"
                                  >
                                    <td className="px-3 py-2 text-gray-900 font-medium">
                                      {line.poLine}
                                    </td>
                                    <td className="px-3 py-2">
                                      {line.partNum ? (
                                        <Link
                                          to={`/parts/${encodeURIComponent(line.partNum)}`}
                                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                        >
                                          {line.partNumUM || line.partNum}
                                        </Link>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700 max-w-xs truncate">
                                      {line.lineDesc}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-900 whitespace-nowrap">
                                      {formatQty(line.vendorQty)} {line.pum}
                                      {line.ium !== line.pum && (
                                        <span className="ml-1 text-xs text-gray-500">
                                          ({formatQty(line.ourQty)} {line.ium})
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                      {t(...RECEIVED_TO_KEYS[line.receivedTo])}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                      {line.jobNum || line.binNum || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {receipts.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              {t('purchaseOrder.noReceipts', 'No receipts found')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
